import { useState } from "react";
import { verifySiteFromBrowser } from "./verifySite.js";

/**
 * The cache key a framed preview is pointed at, and the button that advances it.
 *
 * Both skins frame the deployed site and both offer a reload beside it, so the
 * one thing that makes that button work lives here rather than being written
 * twice: refreshing the browser's copy of the site's assets *before* the frame
 * is re-pointed.
 *
 * A new key only busts the document. The bundles it pulls in keep their own
 * cache entries, so a frame broken by a 404 collected mid-deploy comes back
 * broken however many times it is re-pointed — which is the failure this button
 * exists for. `verifySiteFromBrowser` repairs those entries on its way past
 * (`cache: "reload"`), and running it first means the frame's own load is not
 * spent waiting on the network.
 *
 * The answer is not read: this is the repair, and the frame is being re-pointed
 * either way. A site that still will not serve fails the same way it would have
 * without the button.
 *
 * `loadKey` starts from a clock read once, in state, rather than during render —
 * derived per render it would reload the frame out from under whoever is
 * already reading it. The bump is `max(key + 1, now)` so two presses inside the
 * same millisecond still produce two different URLs.
 */
export function usePreviewFrame(siteUrl: string) {
  const [loadKey, setLoadKey] = useState(() => Date.now());
  const [reloading, setReloading] = useState(false);

  async function reload() {
    setReloading(true);
    await verifySiteFromBrowser(siteUrl);
    setReloading(false);
    setLoadKey((key) => Math.max(key + 1, Date.now()));
  }

  return { loadKey, reloading, reload };
}
